import { useTranslation } from 'react-i18next';
import { WeekFilter } from '../ui/week-filter';
import { matchesWeek } from '../../lib/weekFilter';
import i18n from '../../i18n';
import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Badge } from '../ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { 
  Plus, Search, AlertTriangle, ClipboardCheck, Filter, 
  Clock, User, Calendar, Link2, Trash2, ChevronRight, FolderOpen, Users
} from 'lucide-react';
import { format } from 'date-fns';
import { toast } from 'sonner';
import EntityLinker, { EntityLinkBadge } from './EntityLinker';
import { NCRDetailDialog, CAPADetailDialog } from './ISODetailDialogs';
import { DateText } from '../ui/week-badge';

const API_BASE = process.env.REACT_APP_BACKEND_URL || '';

const NCR_SEVERITY = [
  { value: 'critical', label: i18n.t('auto.lbl.critical'), color: 'bg-red-100 text-red-800' },
  { value: 'major', label: i18n.t('auto.lbl.major'), color: 'bg-orange-100 text-orange-800' },
  { value: 'minor', label: i18n.t('auto.lbl.minor'), color: 'bg-yellow-100 text-yellow-800' },
];

const NCR_STATUS = [
  { value: 'open', label: i18n.t('auto.lbl.open'), color: 'bg-yellow-100 text-yellow-800' },
  { value: 'investigating', label: i18n.t('auto.lbl.investigating'), color: 'bg-blue-100 text-blue-800' },
  { value: 'action_required', label: i18n.t('auto.lbl.actionRequired'), color: 'bg-orange-100 text-orange-800' },
  { value: 'verification', label: i18n.t('auto.lbl.verification'), color: 'bg-purple-100 text-purple-800' },
  { value: 'closed', label: i18n.t('auto.lbl.closed'), color: 'bg-green-100 text-green-800' },
];

const NCR_SOURCES = [
  { value: 'audit', label: i18n.t('auto.lbl.auditFinding') },
  { value: 'customer_complaint', label: i18n.t('auto.lbl.customerComplaint') },
  { value: 'internal', label: i18n.t('auto.lbl.internalDetection') },
  { value: 'supplier', label: i18n.t('auto.lbl.supplierIssue') },
];

const CAPA_TYPES = [
  { value: 'corrective', label: i18n.t('auto.lbl.correctiveAction') },
  { value: 'preventive', label: i18n.t('auto.lbl.preventiveAction') },
];

const CAPA_STATUS = [
  { value: 'open', label: i18n.t('auto.lbl.open'), color: 'bg-yellow-100 text-yellow-800' },
  { value: 'planning', label: i18n.t('auto.lbl.planning'), color: 'bg-blue-100 text-blue-800' },
  { value: 'implementation', label: i18n.t('auto.lbl.implementation'), color: 'bg-purple-100 text-purple-800' },
  { value: 'verification', label: i18n.t('auto.lbl.verification'), color: 'bg-indigo-100 text-indigo-800' },
  { value: 'closed', label: i18n.t('auto.lbl.closed'), color: 'bg-green-100 text-green-800' },
];

const CAPA_PRIORITY = [
  { value: 'low', label: i18n.t('auto.lbl.low'), color: 'bg-gray-100 text-gray-800' },
  { value: 'medium', label: i18n.t('auto.lbl.medium'), color: 'bg-yellow-100 text-yellow-800' },
  { value: 'high', label: i18n.t('auto.lbl.high'), color: 'bg-orange-100 text-orange-800' },
  { value: 'critical', label: i18n.t('auto.lbl.critical'), color: 'bg-red-100 text-red-800' },
];

const NCRCAPAManagement = ({ ncrs, capas, employees, projects, onRefresh, token }) => {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState('ncrs');
  const [searchTerm, setSearchTerm] = useState('');
  const [weekFilter, setWeekFilter] = useState(null);
  const [filterStatus, setFilterStatus] = useState('all');
  const [showCreateNCR, setShowCreateNCR] = useState(false);
  const [showCreateCAPA, setShowCreateCAPA] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  // Detail dialogs
  const [showNCRDetail, setShowNCRDetail] = useState(false);
  const [showCAPADetail, setShowCAPADetail] = useState(false);
  const [selectedNCR, setSelectedNCR] = useState(null);
  const [selectedCAPA, setSelectedCAPA] = useState(null);

  const [ncrForm, setNcrForm] = useState({
    ncr_number: '',
    title: '',
    severity: 'minor',
    description: '',
    url: '',
    source: 'internal',
    detected_date: format(new Date(), 'yyyy-MM-dd'),
    detected_by_id: '',
    department: '',
    process_area: '',
    linked_project_ids: [],
    linked_employee_ids: [],
  });

  const [capaForm, setCapaForm] = useState({
    capa_number: '',
    capa_type: 'corrective',
    title: '',
    url: '',
    source: 'ncr',
    source_reference: '',
    problem_description: '',
    priority: 'medium',
    due_date: '',
    linked_ncr_ids: [],
    linked_project_ids: [],
  });

  const filteredNCRs = ncrs.filter(ncr => {
    const matchesSearch = ncr.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                          ncr.ncr_number.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = filterStatus === 'all' || ncr.status === filterStatus;
    return matchesSearch && matchesStatus && matchesWeek(weekFilter, ncr.detected_date || ncr.created_at);
  });

  const filteredCAPAs = capas.filter(capa => {
    const matchesSearch = capa.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                          capa.capa_number.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = filterStatus === 'all' || capa.status === filterStatus;
    return matchesSearch && matchesStatus && matchesWeek(weekFilter, capa.due_date || capa.created_at);
  });

  const handleCreateNCR = async () => {
    if (!ncrForm.ncr_number || !ncrForm.title || !ncrForm.description) {
      toast.error(t('auto.iso.ncrNumberTitleAndDescriptionAre'));
      return;
    }

    setIsLoading(true);
    try {
      const response = await fetch(`${API_BASE}/api/iso/ncrs`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({
          ...ncrForm,
          detected_date: new Date(ncrForm.detected_date).toISOString(),
        }),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to create NCR');
      }

      toast.success(t('auto.iso.ncrCreatedSuccessfully'));
      setShowCreateNCR(false);
      setNcrForm({
        ncr_number: '',
        title: '',
        severity: 'minor',
        description: '',
        url: '',
        source: 'internal',
        detected_date: format(new Date(), 'yyyy-MM-dd'),
        detected_by_id: '',
        department: '',
        process_area: '',
        linked_project_ids: [],
        linked_employee_ids: [],
      });
      onRefresh();
    } catch (error) {
      toast.error(error.message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleCreateCAPA = async () => {
    if (!capaForm.capa_number || !capaForm.title || !capaForm.problem_description) {
      toast.error(t('auto.iso.capaNumberTitleAndProblemDescription'));
      return;
    }

    setIsLoading(true);
    try {
      const body = {
        ...capaForm,
      };
      if (capaForm.due_date) {
        body.due_date = new Date(capaForm.due_date).toISOString();
      }

      const response = await fetch(`${API_BASE}/api/iso/capas`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify(body),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to create CAPA');
      }

      toast.success(t('auto.iso.capaCreatedSuccessfully'));
      setShowCreateCAPA(false);
      setCapaForm({
        capa_number: '',
        capa_type: 'corrective',
        title: '',
        url: '',
        source: 'ncr',
        source_reference: '',
        problem_description: '',
        priority: 'medium',
        due_date: '',
        linked_ncr_ids: [],
        linked_project_ids: [],
      });
      onRefresh();
    } catch (error) {
      toast.error(error.message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleUpdateStatus = async (type, id, newStatus) => {
    try {
      const endpoint = type === 'ncr' ? 'ncrs' : 'capas';
      const response = await fetch(`${API_BASE}/api/iso/${endpoint}/${id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({ status: newStatus }),
      });

      if (!response.ok) throw new Error('Failed to update status');
      toast.success(t('auto.iso.statusUpdated'));
      onRefresh();
    } catch (error) {
      toast.error(error.message);
    }
  };

  const handleDelete = async (type, id) => {
    if (!window.confirm(`Are you sure you want to delete this ${type.toUpperCase()}?`)) return;

    try {
      const endpoint = type === 'ncr' ? 'ncrs' : 'capas';
      const response = await fetch(`${API_BASE}/api/iso/${endpoint}/${id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` },
      });

      if (!response.ok) throw new Error(`Failed to delete ${type}`);
      toast.success(`${type.toUpperCase()} deleted`);
      onRefresh();
    } catch (error) {
      toast.error(error.message);
    }
  };

  const getEmployeeName = (id) => {
    const emp = employees.find(e => e.id === id);
    return emp?.name || 'Unknown';
  };

  const renderNCRCard = (ncr) => {
    const severityInfo = NCR_SEVERITY.find(s => s.value === ncr.severity);
    const statusInfo = NCR_STATUS.find(s => s.value === ncr.status);

    return (
      <Card 
        key={ncr.id} 
        className="hover:shadow-md transition-shadow cursor-pointer"
        onClick={() => {
          setSelectedNCR(ncr);
          setShowNCRDetail(true);
        }}
      >
        <CardContent className="p-4">
          <div className="flex items-start justify-between gap-2">
            <div className="min-w-0 flex-1">
              <div className="mb-1 flex flex-wrap items-center gap-x-2 gap-y-1">
                <AlertTriangle className={`h-4 w-4 ${ncr.severity === 'critical' ? 'text-red-600' : 'text-yellow-600'}`} />
                <span className="font-mono text-sm text-gray-500">{ncr.ncr_number}</span>
                <Badge className={severityInfo?.color}>{severityInfo?.label}</Badge>
                <Badge className={statusInfo?.color}>{statusInfo?.label}</Badge>
              </div>
              <h3 className="font-medium">{ncr.title}</h3>
              <p className="text-sm text-gray-500 mt-1 line-clamp-2">{ncr.description}</p>
              <div className="flex items-center gap-4 mt-2 text-xs text-gray-400">
                <span className="flex items-center gap-1">
                  <Calendar className="h-3 w-3" />
                  {<DateText value={ncr.detected_date} />}
                </span>
                <span className="flex items-center gap-1">
                  <User className="h-3 w-3" />
                  {getEmployeeName(ncr.detected_by_id)}
                </span>
                {ncr.capa_id && (
                  <span className="flex items-center gap-1 text-blue-500">
                    <Link2 className="h-3 w-3" />
                    {t('auto.iso.capaLinked')}
                  </span>
                )}
              </div>
              {/* Linked entities */}
              {(ncr.linked_project_ids?.length > 0 || ncr.linked_employee_ids?.length > 0) && (
                <div className="flex flex-wrap gap-1 mt-2">
                  {ncr.linked_project_ids?.slice(0, 2).map(id => {
                    const proj = projects.find(p => p.id === id);
                    return proj ? (
                      <EntityLinkBadge key={id} type="project" name={proj.name} />
                    ) : null;
                  })}
                  {ncr.linked_project_ids?.length > 2 && (
                    <Badge variant="outline" className="text-xs">+{ncr.linked_project_ids.length - 2} more</Badge>
                  )}
                  {ncr.linked_employee_ids?.slice(0, 2).map(id => {
                    const emp = employees.find(e => e.id === id);
                    return emp ? (
                      <EntityLinkBadge key={id} type="employee" name={emp.name} />
                    ) : null;
                  })}
                  {ncr.linked_employee_ids?.length > 2 && (
                    <Badge variant="outline" className="text-xs">+{ncr.linked_employee_ids.length - 2} more</Badge>
                  )}
                </div>
              )}
            </div>
            <div className="flex shrink-0 flex-wrap items-center justify-end gap-2">
              <Select
                value={ncr.status}
                onValueChange={(value) => handleUpdateStatus('ncr', ncr.id, value)}
              >
                <SelectTrigger className="h-8 w-[130px] max-w-full text-xs">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {NCR_STATUS.map(s => (
                    <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Button
                variant="ghost"
                size="sm"
                className="text-red-600"
                onClick={() => handleDelete('ncr', ncr.id)}
              >
                <Trash2 className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>
    );
  };

  const renderCAPACard = (capa) => {
    const priorityInfo = CAPA_PRIORITY.find(p => p.value === capa.priority);
    const statusInfo = CAPA_STATUS.find(s => s.value === capa.status);
    const typeInfo = CAPA_TYPES.find(t => t.value === capa.capa_type);

    return (
      <Card 
        key={capa.id} 
        className="hover:shadow-md transition-shadow cursor-pointer"
        onClick={() => {
          setSelectedCAPA(capa);
          setShowCAPADetail(true);
        }}
      >
        <CardContent className="p-4">
          <div className="flex items-start justify-between gap-2">
            <div className="min-w-0 flex-1">
              <div className="mb-1 flex flex-wrap items-center gap-x-2 gap-y-1">
                <ClipboardCheck className="h-4 w-4 text-green-600" />
                <span className="font-mono text-sm text-gray-500">{capa.capa_number}</span>
                <Badge variant="outline">{typeInfo?.label}</Badge>
                <Badge className={priorityInfo?.color}>{priorityInfo?.label}</Badge>
                <Badge className={statusInfo?.color}>{statusInfo?.label}</Badge>
              </div>
              <h3 className="font-medium">{capa.title}</h3>
              <p className="text-sm text-gray-500 mt-1 line-clamp-2">{capa.problem_description}</p>
              <div className="flex items-center gap-4 mt-2 text-xs text-gray-400">
                {capa.due_date && (
                  <span className={`flex items-center gap-1 ${new Date(capa.due_date) < new Date() && capa.status !== 'closed' ? 'text-red-500' : ''}`}>
                    <Clock className="h-3 w-3" />
                    Due: {<DateText value={capa.due_date} />}
                  </span>
                )}
                {capa.actions?.length > 0 && (
                  <span>{capa.actions.length} action(s)</span>
                )}
                {capa.source_reference && (
                  <span className="flex items-center gap-1 text-blue-500">
                    <Link2 className="h-3 w-3" />
                    {capa.source_reference}
                  </span>
                )}
              </div>
            </div>
            <div className="flex shrink-0 flex-wrap items-center justify-end gap-2">
              <Select
                value={capa.status}
                onValueChange={(value) => handleUpdateStatus('capa', capa.id, value)}
              >
                <SelectTrigger className="h-8 w-[130px] max-w-full text-xs">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {CAPA_STATUS.map(s => (
                    <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Button
                variant="ghost"
                size="sm"
                className="text-red-600"
                onClick={() => handleDelete('capa', capa.id)}
              >
                <Trash2 className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>
    );
  };

  return (
    <div className="space-y-4" data-testid="ncr-capa-management">
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <div className="flex flex-col md:flex-row gap-4 justify-between mb-4">
          <TabsList className="flex flex-wrap h-auto gap-1 p-1">
            <TabsTrigger value="ncrs" className="flex items-center gap-2" data-testid="tab-ncrs">
              <AlertTriangle className="h-4 w-4" />
              NCRs ({ncrs.length})
            </TabsTrigger>
            <TabsTrigger value="capas" className="flex items-center gap-2" data-testid="tab-capas">
              <ClipboardCheck className="h-4 w-4" />
              CAPAs ({capas.length})
            </TabsTrigger>
          </TabsList>

          <div className="flex flex-wrap gap-2 w-full md:w-auto">
            <div className="relative flex-1 min-w-[140px]">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
              <Input
                placeholder={t('auto.iso.search')}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-9 w-full sm:w-[200px]"
              />
            </div>
            <WeekFilter value={weekFilter} onChange={setWeekFilter} testId="ncr-capa-week-filter" />
            <Select value={filterStatus} onValueChange={setFilterStatus}>
              <SelectTrigger className="w-[140px] shrink-0">
                <Filter className="h-4 w-4 mr-2" />
                <SelectValue placeholder={t('auto.iso.status')} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{t('auto.iso.allStatus')}</SelectItem>
                {(activeTab === 'ncrs' ? NCR_STATUS : CAPA_STATUS).map(s => (
                  <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            {activeTab === 'ncrs' ? (
              <Button onClick={() => setShowCreateNCR(true)} data-testid="create-ncr-btn" className="shrink-0">
                <Plus className="h-4 w-4 mr-2" />
                {t('auto.iso.newNcr')}
              </Button>
            ) : (
              <Button onClick={() => setShowCreateCAPA(true)} data-testid="create-capa-btn" className="shrink-0">
                <Plus className="h-4 w-4 mr-2" />
                {t('auto.iso.newCapa')}
              </Button>
            )}
          </div>
        </div>

        <TabsContent value="ncrs" className="space-y-4">
          {filteredNCRs.length === 0 ? (
            <Card>
              <CardContent className="py-8 text-center text-gray-500">
                <AlertTriangle className="h-12 w-12 mx-auto mb-4 text-gray-300" />
                <p>{t('auto.iso.noNcrsFound')}</p>
              </CardContent>
            </Card>
          ) : (
            filteredNCRs.map(renderNCRCard)
          )}
        </TabsContent>

        <TabsContent value="capas" className="space-y-4">
          {filteredCAPAs.length === 0 ? (
            <Card>
              <CardContent className="py-8 text-center text-gray-500">
                <ClipboardCheck className="h-12 w-12 mx-auto mb-4 text-gray-300" />
                <p>{t('auto.iso.noCapasFound')}</p>
              </CardContent>
            </Card>
          ) : (
            filteredCAPAs.map(renderCAPACard)
          )}
        </TabsContent>
      </Tabs>

      {/* Create NCR Dialog */}
      <Dialog open={showCreateNCR} onOpenChange={setShowCreateNCR}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{t('auto.iso.createNonConformanceReport')}</DialogTitle>
            <DialogDescription className="sr-only">{t('auto.iso.createNonConformanceReport')}</DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium">{t('auto.iso.ncrNumber')}</label>
                <Input
                  value={ncrForm.ncr_number}
                  onChange={(e) => setNcrForm(prev => ({ ...prev, ncr_number: e.target.value  }))}
                  placeholder="e.g., NCR-2024-001"
                />
              </div>
              <div>
                <label className="text-sm font-medium">{t('auto.iso.severity2')}</label>
                <Select
                  value={ncrForm.severity}
                  onValueChange={(value) => setNcrForm(prev => ({ ...prev, severity: value  }))}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {NCR_SEVERITY.map(s => (
                      <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <label className="text-sm font-medium">{t('auto.iso.title')}</label>
              <Input
                value={ncrForm.title}
                onChange={(e) => setNcrForm(prev => ({ ...prev, title: e.target.value  }))}
                placeholder={t('auto.iso.briefTitleOfTheNonConformance')}
              />
            </div>
            <div>
              <label className="text-sm font-medium">{t('auto.iso.description2')}</label>
              <textarea
                className="w-full min-h-[100px] p-2 border rounded-md text-sm"
                value={ncrForm.description}
                onChange={(e) => setNcrForm(prev => ({ ...prev, description: e.target.value  }))}
                placeholder={t('auto.iso.detailedDescriptionOfTheNonConformance')}
              />
            </div>
            <div>
              <label className="text-sm font-medium">{t('auto.iso.externalUrl')}</label>
              <Input
                value={ncrForm.url}
                onChange={(e) => setNcrForm(prev => ({ ...prev, url: e.target.value  }))}
                placeholder="https://example.com/evidence"
                data-testid="ncr-url-input"
              />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium">{t('auto.iso.source')}</label>
                <Select
                  value={ncrForm.source}
                  onValueChange={(value) => setNcrForm(prev => ({ ...prev, source: value  }))}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {NCR_SOURCES.map(s => (
                      <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-sm font-medium">{t('auto.iso.detectedDate')}</label>
                <Input
                  type="date"
                  value={ncrForm.detected_date}
                  onChange={(e) => setNcrForm(prev => ({ ...prev, detected_date: e.target.value  }))}
                />
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium">{t('auto.iso.detectedBy')}</label>
                <Select
                  value={ncrForm.detected_by_id}
                  onValueChange={(value) => setNcrForm(prev => ({ ...prev, detected_by_id: value  }))}
                >
                  <SelectTrigger>
                    <SelectValue placeholder={t('auto.iso.selectEmployee')} />
                  </SelectTrigger>
                  <SelectContent>
                    {employees.map(emp => (
                      <SelectItem key={emp.id} value={emp.id}>{emp.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-sm font-medium">{t('auto.iso.department')}</label>
                <Input
                  value={ncrForm.department}
                  onChange={(e) => setNcrForm(prev => ({ ...prev, department: e.target.value  }))}
                  placeholder={t('auto.iso.departmentArea')}
                />
              </div>
            </div>
            
            {/* Entity Linking */}
            <div className="border-t pt-4">
              <h4 className="text-sm font-medium mb-3 flex items-center gap-2">
                <Link2 className="h-4 w-4" />
                {t('auto.iso.linkToProjectsEmployees')}
              </h4>
              <EntityLinker
                linkedProjectIds={ncrForm.linked_project_ids}
                linkedEmployeeIds={ncrForm.linked_employee_ids}
                projects={projects}
                employees={employees}
                onProjectsChange={(ids) => setNcrForm(prev => ({ ...prev, linked_project_ids: ids  }))}
                onEmployeesChange={(ids) => setNcrForm(prev => ({ ...prev, linked_employee_ids: ids  }))}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreateNCR(false)} data-testid="dialog-cancel-btn">{t('auto.iso.cancel')}</Button>
            <Button onClick={handleCreateNCR} disabled={isLoading} data-testid="dialog-createncr-btn">
              {isLoading ? t('auto.iso.creating') : t('auto.iso.createNcr')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Create CAPA Dialog */}
      <Dialog open={showCreateCAPA} onOpenChange={setShowCreateCAPA}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{t('auto.iso.createCapa')}</DialogTitle>
            <DialogDescription className="sr-only">{t('auto.iso.createCapa')}</DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium">{t('auto.iso.capaNumber')}</label>
                <Input
                  value={capaForm.capa_number}
                  onChange={(e) => setCapaForm(prev => ({ ...prev, capa_number: e.target.value  }))}
                  placeholder="e.g., CAPA-2024-001"
                />
              </div>
              <div>
                <label className="text-sm font-medium">{t('auto.iso.type3')}</label>
                <Select
                  value={capaForm.capa_type}
                  onValueChange={(value) => setCapaForm(prev => ({ ...prev, capa_type: value  }))}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {CAPA_TYPES.map(t => (
                      <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <label className="text-sm font-medium">{t('auto.iso.title')}</label>
              <Input
                value={capaForm.title}
                onChange={(e) => setCapaForm(prev => ({ ...prev, title: e.target.value  }))}
                placeholder={t('auto.iso.briefTitleOfTheCapa')}
              />
            </div>
            <div>
              <label className="text-sm font-medium">{t('auto.iso.problemDescription')}</label>
              <textarea
                className="w-full min-h-[100px] p-2 border rounded-md text-sm"
                value={capaForm.problem_description}
                onChange={(e) => setCapaForm(prev => ({ ...prev, problem_description: e.target.value  }))}
                placeholder={t('auto.iso.describeTheProblemBeingAddressed')}
              />
            </div>
            <div>
              <label className="text-sm font-medium">{t('auto.iso.externalUrl')}</label>
              <Input
                value={capaForm.url}
                onChange={(e) => setCapaForm(prev => ({ ...prev, url: e.target.value  }))}
                placeholder="https://example.com/reference"
                data-testid="capa-url-input"
              />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium">{t('auto.iso.priority')}</label>
                <Select
                  value={capaForm.priority}
                  onValueChange={(value) => setCapaForm(prev => ({ ...prev, priority: value  }))}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {CAPA_PRIORITY.map(p => (
                      <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-sm font-medium">{t('auto.iso.dueDate')}</label>
                <Input
                  type="date"
                  value={capaForm.due_date}
                  onChange={(e) => setCapaForm(prev => ({ ...prev, due_date: e.target.value  }))}
                />
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium">{t('auto.iso.source')}</label>
                <Select
                  value={capaForm.source}
                  onValueChange={(value) => setCapaForm(prev => ({ ...prev, source: value  }))}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="ncr">{t('auto.iso.ncr')}</SelectItem>
                    <SelectItem value="audit">{t('auto.iso.audit')}</SelectItem>
                    <SelectItem value="customer_feedback">{t('auto.iso.customerFeedback')}</SelectItem>
                    <SelectItem value="internal">{t('auto.iso.internal')}</SelectItem>
                    <SelectItem value="risk_assessment">{t('auto.iso.riskAssessment')}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-sm font-medium">{t('auto.iso.sourceReference')}</label>
                <Input
                  value={capaForm.source_reference}
                  onChange={(e) => setCapaForm(prev => ({ ...prev, source_reference: e.target.value  }))}
                  placeholder="e.g., NCR-2024-001"
                />
              </div>
            </div>
            
            {/* Entity Linking */}
            <div className="border-t pt-4">
              <h4 className="text-sm font-medium mb-3 flex items-center gap-2">
                <Link2 className="h-4 w-4" />
                {t('auto.iso.linkToProjects')}
              </h4>
              <EntityLinker
                linkedProjectIds={capaForm.linked_project_ids}
                linkedEmployeeIds={[]}
                projects={projects}
                employees={employees}
                onProjectsChange={(ids) => setCapaForm(prev => ({ ...prev, linked_project_ids: ids  }))}
                onEmployeesChange={() => {}}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreateCAPA(false)} data-testid="dialog-cancel-btn">{t('auto.iso.cancel')}</Button>
            <Button onClick={handleCreateCAPA} disabled={isLoading} data-testid="dialog-createcapa-btn">
              {isLoading ? t('auto.iso.creating') : t('auto.iso.createCapa')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* NCR Detail Dialog */}
      <NCRDetailDialog
        open={showNCRDetail}
        onOpenChange={setShowNCRDetail}
        ncr={selectedNCR}
        employees={employees}
        projects={projects}
        token={token}
        onSave={() => onRefresh?.()}
        onRefresh={() => onRefresh?.()}
      />

      {/* CAPA Detail Dialog */}
      <CAPADetailDialog
        open={showCAPADetail}
        onOpenChange={setShowCAPADetail}
        capa={selectedCAPA}
        employees={employees}
        projects={projects}
        token={token}
        onSave={() => onRefresh?.()}
        onRefresh={() => onRefresh?.()}
      />
    </div>
  );
};

export default NCRCAPAManagement;
